import { Capacitor } from '@capacitor/core';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { loadPreference, savePreference } from '../lib/preferences';
import {
  DARK_THEMES,
  systemTheme,
  THEME_GROUND,
  THEME_KEY,
  ThemeContext,
  themeSchema,
  type AppliedTheme,
  type ThemeChoice,
} from '../lib/theme';

/**
 * The colour theme for the whole app, the reader included. "System" follows the device's light
 * or dark setting as it changes. The choice is kept on the device.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<ThemeChoice>('system');
  const [system, setSystem] = useState<AppliedTheme>(systemTheme);
  const applied = choice === 'system' ? system : choice;

  useEffect(() => {
    void loadPreference(THEME_KEY, themeSchema, 'system').then(setChoiceState);
    const query = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setSystem(systemTheme());
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    document.documentElement.dataset.theme = applied;
    document
      .querySelector('meta[name="theme-color"]')
      ?.setAttribute('content', THEME_GROUND[applied]);
    if (Capacitor.isNativePlatform()) {
      void StatusBar.setBackgroundColor({ color: THEME_GROUND[applied] }).catch(() => {});
      void StatusBar.setStyle({ style: DARK_THEMES.has(applied) ? Style.Dark : Style.Light }).catch(
        () => {},
      );
    }
  }, [applied]);

  const setChoice = useCallback((next: ThemeChoice) => {
    setChoiceState(next);
    void savePreference(THEME_KEY, next);
  }, []);

  const value = useMemo(() => ({ choice, applied, setChoice }), [choice, applied, setChoice]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
